
// src/ChatDetail.js

import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import './ChatDetail.css';
import api from './api';

import backIcon from './assets/backspace-icon.png';
import placeholderPic from './assets/placeholder-profile.png';

import photoIcon from './assets/photo-icon.png';
import sendIcon from './assets/send-icon.png';
import goldTickIcon from './assets/goldtick-icon.png';

import dotsIcon from './assets/dots-icon.png';
import blockIcon from './assets/block-icon.png';
import blockIconDark from './assets/blockdark-icon.png';


import BlockConfirmDrawer from './components/popups/BlockConfirmDrawer';
import { useTheme } from './context/ThemeContext';

import SharedPostPreview from './components/messages/SharedPostPreview';
import SharedProductPreview from './components/messages/SharedProductPreview';
import AutoExpandTextarea from './components/inputs/AutoExpandTextarea';

function ChatDetail({ currentUser }) {
  const { username } = useParams();
  const navigate = useNavigate();
  const { theme } = useTheme();

  const isDark = theme === 'dark';

  const [chatUser, setChatUser] = useState(null);
  const [newMessage, setNewMessage] = useState('');
  const [messages, setMessages] = useState([]);
  const [hasLoadedMessages, setHasLoadedMessages] = useState(false);

  const [selectedImageFile, setSelectedImageFile] = useState(null);
  const [selectedImagePreview, setSelectedImagePreview] = useState('');

const [sendingImage, setSendingImage] = useState(false);

// 🔒 Privacy state: disables sending when blocked
const [messageBlocked, setMessageBlocked] = useState(false);
const [checkingPrivacy, setCheckingPrivacy] = useState(false);

// 🔒 DM block/unblock menu state
const [iBlockedThem, setIBlockedThem] = useState(false);
const [showChatMenu, setShowChatMenu] = useState(false);
const [showBlockConfirmDrawer, setShowBlockConfirmDrawer] = useState(false);
const [blockActionLoading, setBlockActionLoading] = useState(false);
const [chatToast, setChatToast] = useState({
  visible: false,
  message: '',
  type: '',
});

const messagesEndRef = useRef(null);
const imageInputRef = useRef(null);

const showChatToast = (message, type = 'success', duration = 2200) => {
  setChatToast({ visible: true, message, type });

  window.clearTimeout(showChatToast._t);
  showChatToast._t = window.setTimeout(() => {
    setChatToast({ visible: false, message: '', type: '' });
  }, duration);
};

  const isMerchant =
    chatUser?.userType === 'merchant' &&
    (chatUser?.isMerchant || chatUser?.user?.isMerchant);

  const isAdminUser = (user) => user?.isAdmin === true || !!user?.adminRole;
  const isAppUser = (user) => user?.isApp === true || user?.accountType === 'app';
  const isSpecialUser = (user) => isAdminUser(user) || isAppUser(user);

  const chatUserRole = isAdminUser(chatUser)
    ? 'Admin'
    : isAppUser(chatUser)
    ? 'App'
    : isMerchant
    ? 'Merchant'
    : 'Pioneer';

  const getSpecialTitle = (user) => {
    if (isAdminUser(user)) return user?.adminRole || 'Admin';
    if (isAppUser(user)) return 'App account';
    return '';
  };

  // 👇 Image viewer state
  const [viewerOpen, setViewerOpen] = useState(false);
  const [viewerIndex, setViewerIndex] = useState(0);
  const popHandlerRef = useRef(null);

  // Build a flat list of image message URLs to navigate
  const imageUrls = useMemo(
    () => messages.filter((m) => !!m.image).map((m) => m.image),
    [messages]
  );

  const openViewerAt = (url) => {
    const index = imageUrls.indexOf(url);
    if (index >= 0) {
      try {
        window.history.pushState({ modal: 'chatImage' }, '');
      } catch {}

      setViewerIndex(index);
      setViewerOpen(true);
    }
  };

  const closeViewer = () => {
    setViewerOpen(false);
    try {
      window.history.back();
    } catch {}
  };

  const showPrev = (e) => {
    e?.stopPropagation();
    if (!imageUrls.length) return;
    setViewerIndex((i) => (i - 1 + imageUrls.length) % imageUrls.length);
  };

  const showNext = (e) => {
    e?.stopPropagation();
    if (!imageUrls.length) return;
    setViewerIndex((i) => (i + 1) % imageUrls.length);
  };

  const clearSelectedImage = () => {
    if (selectedImagePreview) {
      URL.revokeObjectURL(selectedImagePreview);
    }
    setSelectedImageFile(null);
    setSelectedImagePreview('');
    if (imageInputRef.current) {
      imageInputRef.current.value = '';
    }
  };

  // When the viewer is open, intercept browser back/edge-swipe
  useEffect(() => {
    if (!viewerOpen) return;

    const onPop = () => {
      setViewerOpen(false);
      window.removeEventListener('popstate', onPop);
      popHandlerRef.current = null;
    };

    popHandlerRef.current = onPop;
    window.addEventListener('popstate', onPop);

    return () => {
      if (popHandlerRef.current) {
        window.removeEventListener('popstate', popHandlerRef.current);
        popHandlerRef.current = null;
      }
    };
  }, [viewerOpen]);

  // Keyboard: Esc/Left/Right
  useEffect(() => {
    if (!viewerOpen) return;

    const onKey = (e) => {
      if (e.key === 'Escape') closeViewer();
      if (e.key === 'ArrowLeft') showPrev();
      if (e.key === 'ArrowRight') showNext();
    };

    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [viewerOpen, imageUrls.length]);

  // Fetch messages + mark read
  useEffect(() => {
    if (!currentUser?.username || !username || hasLoadedMessages) return;

    const fetchMessages = async () => {
      try {
        const res = await api.get(`/messages/${currentUser.username}/${username}`);
        setMessages(res.data || []);
        setHasLoadedMessages(true);
      } catch (err) {
        console.error('❌ Failed to fetch messages:', err);
      }
    };

    const markMessagesAsRead = async () => {
      try {
        await api.post('/messages/mark-read', {
          from: username,
          to: currentUser.username,
        });
      } catch (err) {
        console.error('❌ Failed to mark messages as read:', err);
      }
    };

    fetchMessages();
    markMessagesAsRead();
  }, [currentUser?.username, username, hasLoadedMessages]);

  // Cleanup when switching to another username
  useEffect(() => {
    return () => {
      setMessages([]);
      setHasLoadedMessages(false);
      setViewerOpen(false);
      setViewerIndex(0);

      setSelectedImageFile(null);
      setSelectedImagePreview('');

      if (imageInputRef.current) {
        imageInputRef.current.value = '';
      }
    };
  }, [username]);


// 🔒 Check if this chat is blocked either way
useEffect(() => {
  if (!currentUser?.username || !username) {
    setMessageBlocked(false);
    setCheckingPrivacy(false);
    return;
  }

  if (
    String(currentUser.username).toLowerCase() ===
    String(username).toLowerCase()
  ) {

setMessageBlocked(false);
setIBlockedThem(false);
setCheckingPrivacy(false);
    return;
  }

  let cancelled = false;

  const checkPrivacyStatus = async () => {
    try {
      setCheckingPrivacy(true);

      const res = await api.get(`/privacy/status/${username}`);

      if (cancelled) return;

const iBlocked = res.data?.iBlockedThem === true;

const blocked =
  res.data?.canMessage === false ||
  res.data?.theyBlockedMe === true ||
  iBlocked;

setIBlockedThem(Boolean(iBlocked));
setMessageBlocked(Boolean(blocked));

      // If blocked while an image is selected, clear it so the user cannot send media.
      if (blocked) {
        clearSelectedImage();
      }
    } catch (err) {
      console.error('❌ Failed to check chat privacy:', err);

      if (!cancelled) {
        // Fail open so chat UI does not break if endpoint has an issue.
        // Backend still blocks unsafe sends.

setMessageBlocked(false);
setIBlockedThem(false);
      }
    } finally {
      if (!cancelled) {
        setCheckingPrivacy(false);
      }
    }
  };

  checkPrivacyStatus();

  return () => {
    cancelled = true;
  };
}, [currentUser?.username, username]);


  useEffect(() => {
    return () => {
      if (selectedImagePreview) {
        URL.revokeObjectURL(selectedImagePreview);
      }
    };
  }, [selectedImagePreview]);



const openBlockConfirm = () => {
  if (!currentUser?.username || !username) {
    showChatToast('You need to be logged in to block users.', 'error');
    return;
  }

  if (
    String(currentUser.username).toLowerCase() ===
    String(username).toLowerCase()
  ) {
    showChatToast('You cannot block yourself.', 'error');
    return;
  }

  setShowChatMenu(false);

  setTimeout(() => {
    setShowBlockConfirmDrawer(true);
  }, 120);
};

const confirmToggleBlock = async () => {
  const nextAction = iBlockedThem ? 'unblock' : 'block';

  try {
    setBlockActionLoading(true);

    await api.post(`/privacy/${nextAction}`, {
      targetUsername: username,
    });

    const nextBlockedState = !iBlockedThem;

    setIBlockedThem(nextBlockedState);
    setMessageBlocked(nextBlockedState);
    setShowBlockConfirmDrawer(false);

    if (nextBlockedState) {
      clearSelectedImage();
    }

    showChatToast(
      nextBlockedState
        ? `@${username} has been blocked.`
        : `@${username} has been unblocked.`,
      'success'
    );
  } catch (err) {
    console.error(`❌ Failed to ${nextAction} user:`, err);

    const msg =
      err?.response?.data?.message ||
      err?.response?.data?.error ||
      `Failed to ${nextAction} user. Please try again.`;

    showChatToast(msg, 'error');
  } finally {
    setBlockActionLoading(false);
  }
};


const handleSendMessage = async () => {
  if (messageBlocked || checkingPrivacy) return;

  const trimmedMessage = newMessage.trim();
  if (!trimmedMessage) return;
    console.log('📨 Sending text-only message:', {
      from: currentUser.username,
      to: username,
      message: trimmedMessage,
    });

    try {
      const res = await api.post('/messages', {
        from: currentUser.username,
        to: username,
        message: trimmedMessage,
      });

      const sentMessage = res.data.data;

      if (sentMessage?.type === 'shared_post' && sentMessage?.post) {
        sentMessage.post.name = sentMessage.post.name || currentUser?.name;
        sentMessage.post.profilePic = sentMessage.post.profilePic || currentUser?.profilePic;
        sentMessage.post.adminRole = sentMessage.post.adminRole ?? currentUser?.adminRole ?? null;
        sentMessage.post.isAdmin =
          sentMessage.post.isAdmin ??
          (currentUser?.isAdmin === true || !!currentUser?.adminRole);
        sentMessage.post.accountType =
          sentMessage.post.accountType || currentUser?.accountType || 'primary';
        sentMessage.post.isApp =
          sentMessage.post.isApp ??
          (currentUser?.isApp === true || currentUser?.accountType === 'app');
      }

      if (sentMessage?.type === 'shared_product' && sentMessage?.product) {
        sentMessage.product.ownerName = sentMessage.product.ownerName || currentUser?.name;
        sentMessage.product.profilePic = sentMessage.product.profilePic || currentUser?.profilePic;
        sentMessage.product.storeName = sentMessage.product.storeName || currentUser?.storeName;
        sentMessage.product.businessUsername =
          sentMessage.product.businessUsername || currentUser?.businessUsername;
        sentMessage.product.businessLogo =
          sentMessage.product.businessLogo || currentUser?.businessLogo;
        sentMessage.product.adminRole = sentMessage.product.adminRole ?? currentUser?.adminRole ?? null;
        sentMessage.product.isAdmin =
          sentMessage.product.isAdmin ??
          (currentUser?.isAdmin === true || !!currentUser?.adminRole);
        sentMessage.product.accountType =
          sentMessage.product.accountType || currentUser?.accountType || 'primary';
        sentMessage.product.isApp =
          sentMessage.product.isApp ??
          (currentUser?.isApp === true || currentUser?.accountType === 'app');
      }

      console.log('✅ Text-only message sent:', sentMessage);

      setMessages((prev) => [...prev, sentMessage]);
      setNewMessage('');
    } catch (err) {
      console.error('❌ Failed to send message:', err);
    }
  };

const handlePickImage = (e) => {
  if (messageBlocked || checkingPrivacy) {
    if (e.target) e.target.value = '';
    return;
  }

  const file = e.target.files?.[0];
  if (!file) return;
    if (selectedImagePreview) {
      URL.revokeObjectURL(selectedImagePreview);
    }

    console.log('🖼️ Selected image:', {
      name: file.name,
      type: file.type,
      size: file.size,
    });

    const previewUrl = URL.createObjectURL(file);
    setSelectedImageFile(file);
    setSelectedImagePreview(previewUrl);
  };

const handleSendSelectedImage = async () => {
  if (messageBlocked || checkingPrivacy) return;
  if (!selectedImageFile || sendingImage) return;
    const trimmedMessage = newMessage.trim();

    const formData = new FormData();
    formData.append('from', currentUser.username);
    formData.append('to', username);
    formData.append('type', 'image');
    formData.append('image', selectedImageFile);

    if (trimmedMessage) {
      formData.append('message', trimmedMessage);
    }

    console.log('📨 Sending image message:', {
      from: currentUser.username,
      to: username,
      type: 'image',
      imageName: selectedImageFile?.name,
      caption: trimmedMessage || '(no caption)',
      hasCaption: !!trimmedMessage,
    });

    for (const [key, value] of formData.entries()) {
      console.log('📦 FormData entry:', key, value);
    }

    try {
      setSendingImage(true);

      const res = await api.post('/messages', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });

      console.log('✅ Image message sent:', res.data.data);

      setMessages((prev) => [...prev, res.data.data]);
      setNewMessage('');
      clearSelectedImage();
    } catch (err) {
      console.error('❌ Failed to send image:', err);
      console.error('❌ Image send response:', err?.response?.data);
    } finally {
      setSendingImage(false);
    }
  };

const handlePrimarySend = async () => {
  if (messageBlocked || checkingPrivacy) return;

  console.log('🚀 Primary send clicked:', {
      hasSelectedImage: !!selectedImageFile,
      typedMessage: newMessage,
      trimmedMessage: newMessage.trim(),
    });

    if (selectedImageFile) {
      await handleSendSelectedImage();
      return;
    }

    await handleSendMessage();
  };

  // Auto scroll to bottom
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // Fetch chat user profile
  useEffect(() => {
    const fetchUserProfile = async () => {
      try {
        const res = await api.get(`/users/${username}`);
        setChatUser(res.data);
      } catch (err) {
        console.error('❌ Failed to fetch chat user:', err);
      }
    };

    fetchUserProfile();
  }, [username]);

  // Time formatting for message footer
  const formatMessageTime = (timestamp) => {
    const msgDate = new Date(timestamp);
    return msgDate.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  // Group header label logic
  const getRelativeTimeGroupLabel = (timestamp) => {
    const now = new Date();
    const date = new Date(timestamp);
    const diffMs = now - date;
    const diffMinutes = diffMs / (1000 * 60);
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

    if (diffMinutes < 2) return 'Just now';
    if (diffDays === 0) return 'Today';
    if (diffDays === 1) return 'Yesterday';
    if (diffDays < 7) return `${diffDays} days ago`;
    if (diffDays < 30) return `${Math.floor(diffDays / 7)} week${diffDays < 14 ? '' : 's'} ago`;
    if (diffDays < 365) return `${Math.floor(diffDays / 30)} month${diffDays < 60 ? '' : 's'} ago`;
    return `${Math.floor(diffDays / 365)} year${diffDays < 730 ? '' : 's'} ago`;
  };

  const isStoryShare = (msg) =>
    msg?.type === 'shared_post' && msg?.post?.mediaType?.startsWith('story');

  const isProductShare = (msg) => msg?.type === 'shared_product' && !!msg?.product;

  return (
    <div className="chatdetail-container">
      <div className="chatdetail-topbar">
        <img
          src={backIcon}
          alt="Back"
          className="back-icon-chatdetail"
          onClick={() => navigate(-1)}
        />

        <div className="chatdetail-userbox">
          <img
            src={chatUser?.profilePic || placeholderPic}
            alt="avatar"
            className="chatdetail-avatar"
          />

          <div className="chatdetail-userinfo">
            <span className="chatdetail-name">
              <span className="chatdetail-name-row">
                <span>{chatUser?.name || 'Full Name'}</span>

                {isSpecialUser(chatUser) && (
                  <img
                    src={goldTickIcon}
                    alt={isAdminUser(chatUser) ? 'Admin verified' : 'App verified'}
                    className="chatdetail-gold-tick"
                    title={getSpecialTitle(chatUser)}
                  />
                )}
              </span>
            </span>

            <div className="chatdetail-role-row">
              <span className={chatUserRole === 'Pioneer' ? 'pioneer-pill' : 'merchant-pill'}>
                {chatUserRole}
              </span>
            </div>
          </div>
        </div>

<button
    type="button"
    className="chatdetail-menu-btn"
    onClick={() => setShowChatMenu((v) => !v)}
    aria-label="Open chat menu"
  >
    <img src={dotsIcon} alt="" className="chatdetail-menu-dots-icon" />
  </button>

  {showChatMenu && (
    <div className="chatdetail-menu-popover">
      <button
        type="button"
        className="chatdetail-menu-item danger"
        disabled={blockActionLoading}
        onClick={openBlockConfirm}
      >
        <img
          src={isDark ? blockIconDark : blockIcon}
          alt=""
          className="chatdetail-menu-item-icon"
        />
        <span>
          {blockActionLoading
            ? 'Please wait...'
            : iBlockedThem
            ? `Unblock @${username}`
            : `Block @${username}`}
        </span>
      </button>
    </div>
  )}

      </div>


      <div className="messages-area">
        {(() => {
          let lastGroup = null;

          return messages.map((msg, i) => {
            const groupLabel = getRelativeTimeGroupLabel(msg.timestamp);
            const showGroup = groupLabel !== lastGroup;
            lastGroup = groupLabel;

            const isSent = msg.sender === currentUser.username;

            const isSharedPost = msg.type === 'shared_post' && !!msg.post;
            const isSharedProduct = msg.type === 'shared_product' && !!msg.product;
            const isImageMsg = !!msg.image;

            const isTextOnly =
              !isStoryShare(msg) && !isSharedPost && !isSharedProduct && !isImageMsg;

            return (
              <React.Fragment key={i}>
                {showGroup && <div className="message-group-label">{groupLabel}</div>}

                <div className={`message-wrapper ${isSent ? 'sent' : 'received'}`}>
                  <div
                    className={[
                      'message-bubble',
                      isStoryShare(msg) ? 'story-cmt-bubble' : '',
                      isTextOnly ? 'text-bubble' : 'no-bubble',
                    ].join(' ')}
                  >
                    {isStoryShare(msg) ? (
                      <SharedPostPreview
                        post={msg.post}
                        onOpenPost={() => {
                          const p = msg.post || {};
                          const postId =
                            p._id || p.id || p.postId || (p.post && (p.post._id || p.post.id));

                          if (postId) navigate(`/post/${postId}`);
                          else console.warn('No postId found for shared_post message:', msg);
                        }}
                      />
                    ) : isSharedPost ? (
                      <>
                        <div className="shared-post-label">
                          {isSent ? 'You shared this post' : `${msg.sender} shared a post`}
                        </div>

                        <SharedPostPreview
                          post={msg.post}
                          onOpenPost={() => {
                            const p = msg.post || {};
                            const postId =
                              p._id || p.id || p.postId || (p.post && (p.post._id || p.post.id));

                            if (postId) navigate(`/post/${postId}`);
                            else console.warn('No postId found for shared_post message:', msg);
                          }}
                        />

                        {msg.text && (
                          <div className="message-text" style={{ marginTop: 6 }}>
                            {msg.text}
                          </div>
                        )}
                      </>
                    ) : isSharedProduct || isProductShare(msg) ? (
                      <>
                        <div className="shared-post-label">
                          {isSent ? 'You shared this product' : `${msg.sender} shared a product`}
                        </div>

                        <SharedProductPreview
                          product={msg.product}
                          onOpenProduct={() => {
                            const p = msg.product || {};
                            const productId = p._id || p.id || p.productId || null;

                            if (productId) navigate(`/product/${productId}`);
                            else console.warn('No productId found for shared_product message:', msg);
                          }}
                        />

                        {msg.text && (
                          <div className="message-text" style={{ marginTop: 6 }}>
                            {msg.text}
                          </div>
                        )}
                      </>
                    ) : isImageMsg ? (
                      <div className="image-message-card">
                        <img
                          src={msg.image}
                          alt="chat media"
                          className="message-image"
                          onClick={() => openViewerAt(msg.image)}
                        />

                        {msg.text && (
                          <div className="image-message-caption">
                            {msg.text}
                          </div>
                        )}
                      </div>
                    ) : (
                      msg.text && <div className="message-text">{msg.text}</div>
                    )}
                  </div>

                  <div className="message-time">{formatMessageTime(msg.timestamp)}</div>
                </div>
              </React.Fragment>
            );
          });
        })()}

        <div ref={messagesEndRef} />
      </div>



<div className="send-message-area">
  <div
    className={`chat-composer-pill ${
      messageBlocked ? 'chat-composer-pill--blocked' : ''
    }`}
  >
    {selectedImagePreview && (
      <div className="chat-selected-image-preview">
        <img
          src={selectedImagePreview}
          alt="Selected preview"
          className="chat-selected-image-thumb"
        />

        <button
          type="button"
          className="chat-remove-selected-image"
          onClick={clearSelectedImage}
          aria-label="Remove selected image"
        >
          ✕
        </button>
      </div>
    )}

    <div className="chat-composer-row">
      <img
        src={currentUser?.profilePic || placeholderPic}
        alt=""
        className="chat-composer-avatar"
        onError={(event) => {
          event.currentTarget.src = placeholderPic;
        }}
      />

      <div className="message-input-wrapper">
        <AutoExpandTextarea
          placeholder={
            messageBlocked
              ? 'You cannot send message.'
              : selectedImageFile
              ? 'Add a caption or send image...'
              : 'Type a message...'
          }
          value={newMessage}
          className="chat-input"
          maxVh={0.35}
          minPx={40}
          disabled={messageBlocked || checkingPrivacy}
          onChange={(event) =>
            setNewMessage(event.target.value)
          }
        />
      </div>

      <label
        htmlFor={
          messageBlocked || checkingPrivacy
            ? undefined
            : 'chat-image-upload'
        }
        className={`chat-photo-icon-wrapper ${
          messageBlocked || checkingPrivacy
            ? 'is-disabled'
            : ''
        }`}
        aria-label="Choose an image"
      >
        <img
          src={photoIcon}
          alt=""
          className="chat-photo-icon"
        />
      </label>

      <input
        ref={imageInputRef}
        id="chat-image-upload"
        type="file"
        accept="image/*"
        onChange={handlePickImage}
        className="chat-image-input"
        disabled={
          messageBlocked ||
          checkingPrivacy
        }
      />

      <button
        type="button"
        className="chat-send-icon-btn"
        onClick={handlePrimarySend}
        disabled={
          sendingImage ||
          checkingPrivacy ||
          messageBlocked ||
          (!newMessage.trim() &&
            !selectedImageFile)
        }
        aria-label={
          messageBlocked
            ? 'Messaging blocked'
            : checkingPrivacy
            ? 'Checking messaging permissions'
            : sendingImage
            ? 'Sending message'
            : 'Send message'
        }
      >
        {sendingImage ||
        checkingPrivacy ? (
          <span
            className="chat-send-spinner"
            aria-hidden="true"
          />
        ) : (
          <img
            src={sendIcon}
            alt=""
            className="chat-send-icon"
          />
        )}
      </button>
    </div>
  </div>
</div>


      {viewerOpen && imageUrls.length > 0 && (
        <div className="image-viewer-overlay" onClick={closeViewer}>
          <button
            className="image-viewer-close"
            aria-label="Close"
            onClick={(e) => {
              e.stopPropagation();
              closeViewer();
            }}
          >
            ✕
          </button>

          {imageUrls.length > 1 && (
            <>
              <button
                className="image-viewer-nav prev"
                onClick={showPrev}
                aria-label="Previous image"
              >
                ‹
              </button>
              <button
                className="image-viewer-nav next"
                onClick={showNext}
                aria-label="Next image"
              >
                ›
              </button>
            </>
          )}

          <div className="image-viewer-content" onClick={(e) => e.stopPropagation()}>
            <img src={imageUrls[viewerIndex]} alt="" />
          </div>
        </div>
      )}


<BlockConfirmDrawer
  isOpen={showBlockConfirmDrawer}
  mode={iBlockedThem ? 'unblock' : 'block'}
  targetUsername={username}
  loading={blockActionLoading}
  onClose={() => {
    if (!blockActionLoading) {
      setShowBlockConfirmDrawer(false);
    }
  }}
  onConfirm={confirmToggleBlock}
/>

{chatToast.visible && (
  <div className={`chatdetail-toast ${chatToast.type}`}>
    {chatToast.message}
  </div>
)}

    </div>
  );
}

export default ChatDetail;
